import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  bellLabel,
  canSee,
  notificationAcknowledge,
  notificationResolve,
  notificationSnooze,
  type ApiError,
  type BellItem,
  type BellResponse,
  type NotificationItem,
  type OnCallItem,
  type Role,
  type ViewId,
} from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { useReadOnly } from '../ui/ReadOnly';
import { BellIcon } from './icons';
import { ErrorNote } from '../ErrorNote';

/** Poll the bell this often until the live socket exists. */
export const BELL_POLL_MS = 30_000;

/** Server deep-link views (bell.ts deepLinkFor) to console page paths. */
const DEEP_LINK_VIEW: Record<string, ViewId> = {
  overview: 'overview',
  guardian: 'guardian',
  residents: 'residents',
  oversight: 'oversight',
  monitoring: 'oversight',
  pulse: 'pulse',
  family: 'family',
  worklist: 'worklist',
  policy: 'policy',
  finance: 'finance',
  capture: 'capture',
  sales: 'sales',
  settings: 'settings',
};

const PATH: Partial<Record<ViewId, string>> = {
  overview: '/overview', guardian: '/guardian', residents: '/residents', oversight: '/oversight', pulse: '/pulse',
  family: '/family', worklist: '/worklist', policy: '/policy', finance: '/finance', capture: '/capture', sales: '/sales', settings: '/settings',
};

const SEVERITY_TONE: Record<string, string> = { Critical: 'red', High: 'amber', Medium: 'accent', Info: 'muted' };

function ago(iso: string | null): string {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (!Number.isFinite(m)) return '';
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 48) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

const CHANNEL: Record<string, string> = { push: 'Push', sms: 'SMS', email: 'Email', voice: 'Voice', in_app: 'In app' };
/**
 * Where else the alert went (design chB): every row is in-app, so the line names the other
 * channels; a row that went nowhere else reads In app, or In the portal for a family member.
 */
const channels = (c: string[], family: boolean) => {
  const out = c.filter((x) => x !== 'in_app');
  return out.length ? out.map((x) => CHANNEL[x] ?? x).join(' · ') : family ? 'In the portal' : CHANNEL.in_app;
};

/** Who works an alert in the Notification Center: staff, never a family member or a resident. */
const WORKS_ALERTS = (role: Role | null) => !!role && role !== 'FAMILY_MEMBER' && role !== 'RESIDENT' && role !== 'MEMBER';

const hhmm = (iso: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
};

/** Where an alert stands, in words, once someone has worked it. */
function workedLine(i: BellItem): string | null {
  if (i.resolvedAt || i.status === 'resolved') return 'Resolved';
  if (i.snoozedUntil && Date.parse(i.snoozedUntil) > Date.now()) return `Snoozed until ${hhmm(i.snoozedUntil)}`;
  if (i.acknowledgedAt || i.status === 'acknowledged') return 'Acknowledged';
  return null;
}

export function Bell({ role }: { role: Role | null }) {
  const { client } = useSession();
  // Viewing as another role: the list reads, but nothing is marked read.
  const readOnly = useReadOnly();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const title = bellLabel(role);
  const family = role === 'FAMILY_MEMBER';

  const q = useQuery<BellResponse>((signal) => api.bell(client, { query: { view: 'bell', limit: 20 }, signal }), [client], { pollMs: BELL_POLL_MS });
  const data = q.data;
  const unread = data?.unread ?? (data ? data.items.filter((i) => !i.readAt).length : 0);

  // Close on Escape (focus back to the bell) and on a click outside.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const go = (view: string) => {
    const v = DEEP_LINK_VIEW[view];
    const path = v && canSee(role, v) ? PATH[v] : undefined;
    setOpen(false);
    if (path) navigate(path);
  };

  const openItem = async (item: BellItem) => {
    if (!item.readAt && !readOnly) {
      const stamp = new Date().toISOString();
      q.setData((p) => (p ? { ...p, unread: p.unread === null ? null : Math.max(0, p.unread - 1), items: p.items.map((i) => (i.id === item.id ? { ...i, readAt: stamp } : i)) } : p));
      try {
        const r = await api.markRead(client, { params: { id: item.id } });
        q.setData((p) => (p ? { ...p, items: p.items.map((i) => (i.id === item.id ? { ...i, readAt: r.readAt ?? stamp } : i)) } : p));
      } catch {
        q.reload();
      }
    }
    go(item.deepLink.view);
  };

  // Notification Center actions (the Flutter console's Acknowledge, Snooze and Resolve).
  const [working, setWorking] = useState<string | null>(null);
  const [workErr, setWorkErr] = useState<ApiError | null>(null);
  const canWork = WORKS_ALERTS(role) && !readOnly;
  const work = async (item: BellItem, fn: () => Promise<NotificationItem>) => {
    if (!canWork || working) return;
    setWorking(item.id);
    setWorkErr(null);
    try {
      const r = await fn();
      q.setData((p) => (p ? { ...p, items: p.items.map((i) => (i.id === item.id ? { ...i, status: r.status, acknowledgedAt: r.acknowledgedAt, snoozedUntil: r.snoozedUntil, resolvedAt: r.resolvedAt } : i)) } : p));
    } catch (e) {
      setWorkErr(e as ApiError);
    } finally {
      setWorking(null);
    }
  };

  const readAll = async () => {
    const stamp = new Date().toISOString();
    q.setData((p) => (p ? { ...p, unread: 0, items: p.items.map((i) => (i.readAt ? i : { ...i, readAt: stamp })) } : p));
    try {
      await api.markAllRead(client);
    } catch {
      q.reload();
    }
  };

  const items = data ? [...data.items].sort((a, b) => Number(!!a.readAt) - Number(!!b.readAt) || Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? '')) : [];
  const onCall: OnCallItem[] = data?.onCall.items ?? [];
  const switchedOff = data?.switchedOffCount ?? 0;
  const empty = data && items.length === 0 && onCall.length === 0;
  const buttonLabel = unread > 0 ? `${title}, ${unread} unread` : title;

  return (
    // The panel hangs from the top bar's cluster (bell, role chip, Sign out), right-aligned
    // with Sign out as in the design (T184: right:0 of the wrapper that holds all three);
    // only the unread count hangs from the bell itself.
    <span className="bell" ref={wrapRef} style={{ position: 'static' }}>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={buttonRef}
        type="button"
        className="iconbtn"
        aria-label={buttonLabel}
        title={title}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        <BellIcon />
      </button>
      {unread > 0 && (
        <span className="bell__count" aria-hidden="true" data-testid="bell-count">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
      </span>
      {open && (
        <div id={panelId} className="bell__panel" role="dialog" aria-label={title}>
          <div className="bell__head">
            <span className="bell__title">{title}</span>
            <span className="bell__meta">{unread ? `${unread} unread` : items.length ? 'all read' : ''}</span>
            <span className="spacer" />
            {unread > 0 && !readOnly && (
              // The design's link sits on the title's baseline with no padding of its own.
              <button type="button" className="linkbtn" style={{ padding: 0, lineHeight: 'inherit' }} onClick={readAll}>
                Mark all read
              </button>
            )}
          </div>
          {q.error && !data && <ErrorNote error={q.error} onRetry={q.reload} compact />}
          {q.loading && !data && !q.error && (
            <div className="bell__skeleton" aria-busy="true" aria-label="Loading alerts">
              <span />
              <span />
            </div>
          )}
          {empty && (
            <p className="bell__empty">
              {switchedOff
                ? `Nothing to show. ${switchedOff} alert${switchedOff === 1 ? ' is' : 's are'} switched off in your notification settings.`
                : 'You’re all caught up.'}
            </p>
          )}
          {onCall.length > 0 && (
            <>
              <span className="bell__group">On-call</span>
              <ul className="bell__list">
                {onCall.map((p) => (
                  <li key={p.id}>
                    <button type="button" className="bell__row bell__row--unread" onClick={() => go(p.deepLink.view)}>
                      <span className={`bell__dot sev-${SEVERITY_TONE[p.severityLevel] ?? 'muted'}`} aria-hidden="true" />
                      <span className="bell__body">
                        <span className="bell__what">{p.headline}</span>
                        <span className="bell__where">
                          {p.sourcePage} {'·'} {ago(p.createdAt)} {'·'} {p.acknowledgedAt ? 'acknowledged' : 'open until someone acknowledges'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {items.length > 0 && (
            <ul className="bell__list" aria-label={`${title} list`}>
              {items.map((item, i) => {
                const head = i === 0 ? (item.readAt ? 'Earlier' : 'New') : !!item.readAt !== !!items[i - 1].readAt ? 'Earlier' : null;
                return (
                  <li key={item.id}>
                    {head && <span className="bell__group">{head}</span>}
                    <button
                      type="button"
                      className={`bell__row${item.readAt ? '' : ' bell__row--unread'}`}
                      onClick={() => openItem(item)}
                      data-testid={`bell-item-${item.id}`}
                      data-read={item.readAt ? 'true' : 'false'}
                    >
                      {/* A family member's updates are one group, drawn green (design GC.Family). */}
                      <span
                        className={`bell__dot sev-${SEVERITY_TONE[item.severityLevel] ?? 'muted'}`}
                        style={family ? { background: 'var(--color-green)' } : undefined}
                        aria-hidden="true"
                      />
                      <span className="bell__body">
                        {/* Balanced line breaks, as the design's rows (whatStyle text-wrap:pretty). */}
                        <span className="bell__what" style={{ textWrap: 'pretty' }}>
                          {!item.readAt && <span className="sr-only">Unread. </span>}
                          <span className="sr-only">{item.severityLevel}. </span>
                          {item.headline ?? item.eventKey.split(':').slice(1).join(':')}
                        </span>
                        <span className="bell__where">
                          {item.sourcePage}
                          {item.subjectName ? ` · ${item.subjectName}` : ''} {'·'} {ago(item.createdAt)}
                        </span>
                      </span>
                      <span className="bell__channels">{channels(item.channels, family)}</span>
                    </button>
                    {canWork && (() => {
                      const done = workedLine(item);
                      const resolved = done === 'Resolved';
                      return (
                        <span className="bell__acts" data-testid={`bell-acts-${item.id}`}>
                          {done && <span className="bell__state">{done}</span>}
                          {!resolved && !item.acknowledgedAt && (
                            <button type="button" className="linkbtn" disabled={working === item.id} onClick={() => void work(item, () => notificationAcknowledge(client, { params: { id: item.id } }))}>
                              Acknowledge
                            </button>
                          )}
                          {!resolved && (
                            <button type="button" className="linkbtn" disabled={working === item.id} onClick={() => void work(item, () => notificationSnooze(client, { params: { id: item.id }, body: { minutes: 60 } }))}>
                              Snooze 1h
                            </button>
                          )}
                          {!resolved && (
                            <button type="button" className="linkbtn" disabled={working === item.id} onClick={() => void work(item, () => notificationResolve(client, { params: { id: item.id } }))}>
                              Resolve
                            </button>
                          )}
                        </span>
                      );
                    })()}
                  </li>
                );
              })}
            </ul>
          )}
          {workErr && <ErrorNote error={workErr} compact />}
          {switchedOff > 0 && !empty && <p className="bell__muted">{switchedOff} more switched off in settings</p>}
          {/* A family member's list has no settings link (design obSetShow: !consumer). */}
          {canSee(role, 'settings') && !family && (
            <button type="button" className="bell__settings" onClick={() => { setOpen(false); navigate('/settings'); }}>
              Notification settings {'→'}
            </button>
          )}
        </div>
      )}
    </span>
  );
}
